/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, Component, ElementRef, HostListener, Inject, Input, OnChanges, ViewChild, signal,
} from '@angular/core';
import {
  itemCommentCount, itemGlyph, itemLikeCount, itemRoute, itemTypeLabel, shorten,
} from '../../common/community/feed/community-item';

const STATLESS_TYPES = ['project', 'referral_campaign', 'course'];
const HEADLINE_LENGTH = 140;
const EXCERPT_LENGTH = 220;

// Bodies can hold escaped markup ("&lt;h3&gt;"), so parse twice: once for the tags, once for what the entities decode to.
function plainText(html: string): string {
  const parse = (value: string) => new DOMParser().parseFromString(value, 'text/html').body.textContent || '';
  return parse(parse(html)).replace(/\s+/g, ' ').trim();
}

interface FeaturedSlide {
  item: any;
  headline: string;
  excerpt: string;
}

// "Featured" card for pinned community items: one full-width lead story at a time.
@Component({
  selector: 'ut-community-featured',
  templateUrl: './community-featured.component.html',
  styleUrls: ['./community-featured.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommunityFeaturedComponent implements OnChanges {
  // undefined while loading; an empty list hides the card.
  @Input() feed: any[] | undefined;
  @Input() skipTarget: HTMLElement | undefined;

  @ViewChild('list') list: ElementRef<HTMLElement> | undefined;

  slides: FeaturedSlide[] = [];
  current = signal(0);
  // The slide the arrows are scrolling to, until it's reached.
  private target: number | null = null;
  private targetTimer: ReturnType<typeof setTimeout> | undefined;

  readonly typeLabel = itemTypeLabel;
  readonly route = itemRoute;
  readonly glyph = itemGlyph;
  readonly likeCount = itemLikeCount;
  readonly commentCount = itemCommentCount;

  constructor(@Inject('$filter') private $filter: any) {}

  isFirst(): boolean {
    return this.current() === 0;
  }

  isLast(): boolean {
    return this.current() === this.slides.length - 1;
  }

  ngOnChanges(): void {
    this.slides = (this.feed || []).map((item) => this.toSlide(item));
    this.current.set(0);
  }

  @HostListener('window:resize')
  updateCurrent(): void {
    const el = this.list?.nativeElement;
    if (!el || !el.clientWidth) return;
    const shown = Math.round(el.scrollLeft / el.clientWidth);
    // While the arrows' smooth scroll runs, the position passes back through the old slide; keep the
    // counter on the target instead of flickering between the two.
    if (this.target !== null) {
      if (shown === this.target) this.target = null;
      return;
    }
    this.current.set(shown);
  }

  goTo(index: number): void {
    // The arrows stay focusable at the ends (aria-disabled), so ignore presses past them.
    if (index < 0 || index >= this.slides.length) return;
    const el = this.list.nativeElement;
    this.target = index;
    // A swipe can interrupt the smooth scroll before it lands; stop waiting for it after a moment.
    clearTimeout(this.targetTimer);
    this.targetTimer = setTimeout(() => {
      this.target = null;
      this.updateCurrent();
    }, 1000);
    el.scrollTo({ left: index * el.clientWidth, behavior: 'smooth' });
    this.current.set(index);
  }

  showStats(item: any): boolean {
    return !STATLESS_TYPES.includes(item.type);
  }

  authorName(item: any): string {
    if (item.type === 'referral_campaign') return 'uTest Community Management';
    if (item.type === 'project') return item.urgent ? 'Urgent Project' : 'Ongoing Project';
    return item.user?.name;
  }

  skipFeatured(event: Event): void {
    event.preventDefault();
    this.skipTarget?.focus();
  }

  // Items with a title lead with it and show their body as an excerpt; statuses lead with their text.
  private toSlide(item: any): FeaturedSlide {
    const body = ['article', 'topic'].includes(item.type)
      ? this.$filter('uMarkdown')(item.content)
      : item.content || item.description;
    const text = plainText(body || '');
    return item.title
      ? { item, headline: item.title, excerpt: shorten(text, EXCERPT_LENGTH) }
      : { item, headline: shorten(text, HEADLINE_LENGTH), excerpt: '' };
  }
}
